/* Resolve o CONTEXTO de cada painel: os ids e os nomes de rede, região, escola
   e turma que as telas precisam.

   De onde vêm os ids
   ------------------
   Do Redux do AVA: cada entrada de `user.user_roles` traz `group_id`,
   `region_id` e `school_id` (ver lib/escopo.js). Isso cobre gestor de rede
   (papel 2), regional (3) e administrador escolar (4).

   O professor (papel 6) é diferente: ele tem várias turmas, e a turma não está
   em user_roles. Então ele ESCOLHE, e a escolha fica no contexto persistido do
   PerfilContext.

   De onde vêm os nomes
   --------------------
   De `api.professorFiltros()`, que devolve as listas de grupos, regionais e
   escolas com nome — uma chamada resolve todos os rótulos.

   ⚠️ Modo mock e os ids reais
   ---------------------------
   Os ids do usuário no AVA (ex.: group_id 28) não existem nas fixtures, que
   vieram do DEMO_MODE do backend do Analytics (grupo 1, escolas 3001+). Se
   usássemos os ids reais contra o mock, nada casaria e todo painel ficaria
   vazio.

   Então, com o mock ligado, o escopo é resolvido a partir das próprias
   fixtures. A MECÂNICA de leitura do Redux continua exercitada — quando o
   backend for portado e o flag desligado, os ids reais passam a valer sem
   mudança de código. É a única concessão do modo mock, e está isolada aqui. */

import { useMemo } from "react";

import { useSelector } from "react-redux";

import { api } from "./api";
import { escopoDoUsuario } from "./escopo";
import { MOCK_LIGADO } from "./transport";
import { useAsync } from "./useAsync";

export function useContextoIE(perfil) {
  const userRoles = useSelector(state => state.user.user_roles);
  const activeRoles = useSelector(state => state.general.active_roles);
  const grupoNomeAtivo = activeRoles?.group_name;

  // Passa o activeRoles INTEIRO, não só o role_id: o grupo/região/escola que o
  // usuário escolheu no modal "Escolha sua rede" mora ali, e é o que vale sobre
  // o vínculo. Ver a nota em lib/escopo.js.
  const escopo = useMemo(
    () => escopoDoUsuario(userRoles, activeRoles),
    [userRoles, activeRoles]
  );

  const filtros = useAsync(() => api.professorFiltros(), []);

  const contexto = useMemo(() => {
    const f = filtros.data;
    if (!f) return null;

    const grupos = f.grupos || [];
    const regionais = f.regionais || [];
    const escolas = f.escolas || [];

    // --- resolução dos ids ---
    let grupoId = escopo.grupoId;
    let regiaoId = escopo.regiaoId;
    let escolaId = escopo.escolaId;

    if (MOCK_LIGADO) {
      // ids das fixtures, para o painel ter dado que casa
      grupoId = grupos[0]?.id ?? grupoId;
      if (perfil === "gestor" && escopo.escopo === "regiao") {
        regiaoId = regionais[0]?.id ?? regiaoId;
      }
      if (perfil === "admin") {
        const daRegiao = regiaoId
          ? escolas.filter(e => e.region_id === regiaoId)
          : escolas;
        escolaId = daRegiao[0]?.id ?? escolas[0]?.id ?? escolaId;
      }
    }

    const nomeDe = (lista, id) => lista.find(x => x.id === id)?.nome;

    return {
      anos: f.anos?.map(a => a.ano) || [],
      grupoId,
      // Sem grupo é o "Todos" do modal, e só o Administrador Educandus chega
      // aqui assim (escopo "todos"): as chamadas saem sem `grupo_id` — o
      // querystring omite null — e o backend, irrestrito, devolve a base
      // inteira. O rótulo tem de dizer isso, senão a tela mostra "Rede" com os
      // números de todas elas somados.
      grupoNome:
        nomeDe(grupos, grupoId) ||
        grupoNomeAtivo ||
        (escopo.escopo === "todos" ? "Todas as redes" : "Rede"),
      regiaoId,
      regiaoNome: nomeDe(regionais, regiaoId),
      escolaId,
      escolaNome: nomeDe(escolas, escolaId),
      // escopo do papel: "rede" | "regiao" | "escola" | "turma"
      escopo: escopo.escopo,
      // true quando o painel do gestor deve ficar preso a uma região (papel 3)
      regiaoFixa: escopo.escopo === "regiao" && !!regiaoId
    };
  }, [filtros.data, escopo, perfil, grupoNomeAtivo]);

  return { contexto, carregando: filtros.loading, erro: filtros.error };
}
